import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StarRating, StarPicker } from "@/components/StarRating";

/**
 * Reviews under a product: the live average, what buyers wrote, and a form for
 * shoppers who have actually paid for this product.
 */
export const ProductReviews = ({ productId, onSummary }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ rating: { average: 0, count: 0 }, reviews: [] });
  const [canReview, setCanReview] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/products/${productId}/reviews`).then(({ data }) => {
    setData(data);
    onSummary?.(data.rating);
  }).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);
  useEffect(() => {
    if (!user) return setCanReview(false);
    api.get("/products/reviewable")
      .then(({ data }) => setCanReview((data.product_ids || []).includes(productId)))
      .catch(() => setCanReview(false));
  }, [user, productId]);

  const submit = async () => {
    if (!rating) return toast.error("Pick a star rating first");
    setSaving(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, comment });
      toast.success("Thank you — your review is live");
      setRating(0); setComment(""); setCanReview(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-16 border-t border-border pt-12" data-testid="pd-reviews">
      <div className="flex items-baseline gap-4 flex-wrap mb-8">
        <h2 className="font-display text-3xl">Customer reviews</h2>
        <StarRating {...data.rating} testid="pd-reviews-rating" />
      </div>

      {canReview && (
        <div className="border border-border rounded-2xl p-6 mb-8 max-w-xl" data-testid="pd-review-form">
          <div className="text-sm mb-3">You bought this — how did you find it?</div>
          <StarPicker value={rating} onChange={setRating} testid="pd-review-stars" />
          <Textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Anything you'd tell another shopper (optional)"
            className="bg-transparent mt-4"
            data-testid="pd-review-comment"
          />
          <Button onClick={submit} disabled={saving} className="btn-pill bg-primary text-primary-foreground mt-4" data-testid="pd-review-submit">
            {saving ? "Posting…" : "Post review"}
          </Button>
        </div>
      )}
      {!user && (
        <p className="text-sm text-muted-foreground mb-8" data-testid="pd-review-signin">
          <Link to="/login" className="text-primary hover:underline">Sign in</Link> to review a product you have bought.
        </p>
      )}

      {data.reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="pd-reviews-empty">
          No reviews yet — be the first to tell others about it.
        </p>
      ) : (
        <div className="space-y-5 max-w-2xl" data-testid="pd-reviews-list">
          {data.reviews.map((r) => (
            <div key={r.id} className="border-b border-border pb-5" data-testid={`pd-review-${r.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-medium text-sm">{r.user_name || "Verified buyer"}</span>
                <StarRating average={r.rating} count={1} size={12} compact testid={`pd-review-stars-${r.id}`} />
                <span className="text-xs text-muted-foreground ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              {r.comment && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ProductReviews;
